import React, { ReactElement, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Drawer from '@mui/material/Drawer'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import CssBaseline from '@mui/material/CssBaseline'
import IconButton from '@mui/material/IconButton'
import { CalendarMonthRounded, ChangeCircleRounded, HelpOutlineRounded } from '@mui/icons-material'
import {
  Container,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
  ListSubheader,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import { groupBy } from 'lodash'
import { useAppSelector, useAppDispatch } from '../../store/hooks'
import { ReactComponent as DateChangeIcon } from '../../assets/menu/Date_change_icon.svg'
import {
  changeCurrentLanguage,
  setModulesSummary,
  setModulesInfo,
  setModules,
  setRuns,
  startWalkthrough,
  changeWalkthroughStatus,
  changeWalkthroughVideo,
  changeCurrentRun,
} from '../../store/actions'
import FsNavigation from '../fs-navigation/fs-navigation'
import { WALKTHROUGH_STATUS } from '../../shared/constants'
import { ReactComponent as FairSolutionLogo } from '../../assets/Logo_FAIR.svg'
import { ReactComponent as FairSolutionLogoDark } from '../../assets/Logo_FAIR_dark.svg'
import { ReactComponent as LanguageLogo } from '../../assets/walkthrough/language.svg'
import FsWalkthrough from '../common/molecules/Walkthrough'
import { updateProfile } from '../../store/actions/user/thunk'
import { formatDate } from '../../shared/utils'
import FsAppsMenu from './AppsMenu'
import FsAccountMenu from './AccountMenu'
import { getIsDemoAccount } from '../../utils'
import FsDemoAccountModal from '../common/molecules/DemoAccountModal'
import { FsMenuItemChildren } from '../fs-menu-item/fs-menu-item'
import { Module } from '../../shared/model/models'

const FsDrawer = ({ fullScreen = false }: { fullScreen?: boolean }) => {
  const [helpAnchorEl, setHelpAnchorEl] = useState<null | HTMLElement>(null)
  const [dateToChange, setDateToChange] = useState<string | null>(null)
  const [shouldHideDate, setShouldHideDate] = useState<boolean>(false)
  const [shouldShowGroups, setShouldShowGroups] = useState<boolean>(false)
  const [isMenuHovered, setIsMenuHovered] = useState<boolean>(false)
  const [isShowText, setIsShowText] = useState<boolean>(false)

  const { token, account, currentTheme, currentLanguage } = useAppSelector(
    (state) => state.currentUser,
  )
  const { modulesSummary, modules, runs, currentRun, modulesInfo } = useAppSelector(
    (state) => state.currentAnalysis,
  )
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { i18n, t } = useTranslation()
  const theme = useTheme()
  const largerThanMid = useMediaQuery(theme.breakpoints.up('sm'))
  const [openDemoModal, setOpenDemoModal] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [curModule, setCurModule] = useState<Module | null>(null)
  const open = Boolean(anchorEl)

  const isDemoAccount = getIsDemoAccount()

  const handleHelpMenu = (event: React.MouseEvent<HTMLElement>) => {
    setHelpAnchorEl(event.currentTarget)
  }
  const handleHelpClose = () => {
    setHelpAnchorEl(null)
  }

  const switchLanguage = (l: 'de' | 'en') => {
    i18n.changeLanguage(l)
    document.documentElement.setAttribute('lang', l)
    dispatch(changeCurrentLanguage(l))
  }

  useEffect(() => {
    i18n.changeLanguage(currentLanguage)
    document.documentElement.setAttribute('lang', currentLanguage)
  }, [])

  const isFsApp = account?.profile.appAccess?.includes('fs')

  useEffect(() => {
    if (isFsApp) {
      dispatch(setRuns())
      dispatch(setModules())
      dispatch(changeWalkthroughStatus(WALKTHROUGH_STATUS.Done))
    } else {
      navigate('/ines/home')
    }
  }, [isFsApp])

  useEffect(() => {
    if (modulesSummary && modulesSummary.length > 0 && currentRun && !modulesInfo) {
      dispatch(setModulesInfo({ date: currentRun.runAt }))
    }
  }, [modulesSummary, currentRun])

  const location = useLocation()

  const latestRun = runs ? runs[runs.length - 1] : null

  const restartWalkthrough = () => {
    dispatch(startWalkthrough())
  }

  const showWalkthroughVideo = () => {
    dispatch(changeWalkthroughVideo(true))
  }

  const dateChangeConfirmed = () => {
    if (runs && dateToChange) {
      dispatch(changeCurrentRun(runs.filter((r) => r.id === parseInt(dateToChange, 10))[0]))
    }
    setDateToChange(null)
  }

  useEffect(() => {
    if (currentRun) {
      dispatch(setModulesSummary({ date: currentRun.runAt }))
    }
  }, [currentRun])

  const filteredRuns = runs ? runs?.filter((r) => r !== null || r !== undefined) : []

  const groupedDates = groupBy(filteredRuns.reverse(), (run) =>
    formatDate(run.runAt, currentLanguage, {
      year: 'numeric',
    }),
  )

  const groupedDateComponents: ReactElement[] = []
  Object.keys(groupedDates)
    .sort((a, b) => parseInt(b, 10) - parseInt(a, 10))
    .forEach((year, yindex) => {
      groupedDateComponents.push(<ListSubheader key={year}>{year}</ListSubheader>)
      groupedDates[year].forEach((run, index) => {
        groupedDateComponents.push(
          <MenuItem key={`${year}_${index}`} value={`${run.id}`}>{`${formatDate(
            run.runAt,
            currentLanguage,
          )}${index === 0 && yindex === 0 ? ` (${t('latest.date')})` : ''}`}</MenuItem>,
        )
      })
    })

  useEffect(() => {
    const path = location.pathname
    if (
      path.startsWith('/fs/report') ||
      path.startsWith('/fs/reports/table') ||
      path.startsWith('/fs/help') ||
      path.startsWith('/fs/etl')
    ) {
      setShouldHideDate(true)
    } else setShouldHideDate(false)
  }, [location])

  const noAnalysisYet = runs && runs.length === 0

  useEffect(() => {
    const path = location.pathname
    if (
      !path.startsWith('/fs/etl') &&
      !path.startsWith('/fs/help') &&
      !path.startsWith('/fs/admin/import') &&
      !path.startsWith('/fs/admin/config') &&
      noAnalysisYet
    ) {
      navigate('/fs/etl')
    }
  }, [location, runs])

  useEffect(() => {
    const path = location.pathname
    if (path.startsWith('/fs/fair-pay')) {
      setShouldShowGroups(true)
      setCurModule(modules?.find((module) => module.name === 'pay') ?? null)
    } else if (path.startsWith('/fs/fair-career')) {
      setShouldShowGroups(true)
      setCurModule(modules?.find((module) => module.name === 'career') ?? null)
    } else if (path.startsWith('/fs/fair-working_time')) {
      setShouldShowGroups(true)
      setCurModule(modules?.find((module) => module.name === 'working_time') ?? null)
    } else if (path.startsWith('/fs/fair-training')) {
      setShouldShowGroups(true)
      setCurModule(modules?.find((module) => module.name === 'training') ?? null)
    } else {
      setShouldShowGroups(false)
      setCurModule(null)
    }
  }, [location, runs, modules])

  return (
    <Box
      sx={{
        display: 'flex',
        backgroundColor: currentTheme === 'light' ? theme.palette.grey[50] : '',
      }}
    >
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          zIndex: 1600,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          margin: 0.5,
          width: 'calc(100% - 8px)',
        }}
        elevation={3}
      >
        <Toolbar
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 1600,
            paddingLeft: 16,
            paddingRight: 16,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {token && <FsAppsMenu />}
            <div
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              onClick={() => navigate('/fs/dashboard')}
            >
              {currentTheme === 'dark' ? (
                <FairSolutionLogoDark width="auto" height="35px" />
              ) : (
                <FairSolutionLogo width="auto" height="35px" />
              )}
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {token && (
              <>
                {shouldShowGroups && curModule && (
                  <div>
                    <Button
                      variant="outlined"
                      endIcon={<ChangeCircleRounded sx={{ color: theme.palette.text.secondary }} />}
                      onClick={(event) =>
                        anchorEl ? setAnchorEl(null) : setAnchorEl(event.currentTarget)
                      }
                      aria-controls="nav-positioned-menu"
                      sx={{
                        mt: 0.6,
                        mr: 0.5,
                        py: 1,
                        color: theme.palette.text.primary,
                        borderColor: theme.palette.action.disabled,
                        '&:hover': {
                          borderColor: theme.palette.text.primary,
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      <Typography>{t('change.compare.group')}</Typography>
                    </Button>
                    <FsMenuItemChildren
                      anchorEl={anchorEl}
                      open={open}
                      handleClose={() => setAnchorEl(null)}
                      module={curModule}
                      isTopMenu
                    />
                  </div>
                )}
                {latestRun && !shouldHideDate && (
                  <div
                    style={{
                      paddingTop: '4px',
                      paddingRight: '4px',
                      marginRight: '8px',
                    }}
                  >
                    <FormControl className="maindate" sx={{ m: 1, width: '100%' }} size="small">
                      <InputLabel
                        id="dependent-characteristic-label"
                        sx={{
                          backgroundColor: theme.palette.background.paper,
                          paddingRight: 1,
                          paddingLeft: 1,
                          borderRadius: 1,
                        }}
                      >
                        {t('data.from')}
                      </InputLabel>
                      {runs && runs.length > 0 && (
                        <Select
                          value={`${currentRun?.id}`}
                          id="run"
                          label="Run"
                          IconComponent={() => null}
                          onChange={(e: SelectChangeEvent) => {
                            if (isDemoAccount) {
                              setOpenDemoModal(true)
                              return
                            }
                            setDateToChange(e.target.value)
                          }}
                          renderValue={(idr) => {
                            const rr = runs.filter((r) => r.id === parseInt(idr, 10))[0]
                            return rr ? (
                              <Stack
                                flexDirection="row"
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <Typography>{`${formatDate(rr.runAt, currentLanguage, {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                })}${
                                  rr.id === latestRun.id ? ` (${t('latest.date')})` : ''
                                }`}</Typography>
                                <CalendarMonthRounded
                                  sx={{ mr: -3, color: theme.palette.text.secondary }}
                                />
                              </Stack>
                            ) : null
                          }}
                          MenuProps={{
                            sx: { marginTop: 2 },
                          }}
                        >
                          {groupedDateComponents}
                        </Select>
                      )}
                    </FormControl>
                  </div>
                )}
                {location.pathname !== '/fs/help' && (
                  <div
                    className="help_walkthrough"
                    style={{
                      borderRight: `1px solid ${theme.palette.action.disabled}`,
                      paddingRight: '4px',
                      marginRight: '4px',
                      marginTop: '2px',
                    }}
                  >
                    <IconButton
                      size="small"
                      aria-label="help of current user"
                      aria-controls="menu-appbar"
                      aria-haspopup="true"
                      sx={{
                        color: theme.palette.text.secondary,
                      }}
                      onClick={handleHelpMenu}
                    >
                      <HelpOutlineRounded />
                    </IconButton>
                    <Menu
                      id="menu-appbar"
                      anchorEl={helpAnchorEl}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                      keepMounted
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                      open={Boolean(helpAnchorEl)}
                      onClose={handleHelpClose}
                      onClick={handleHelpClose}
                      sx={{ marginTop: 2 }}
                    >
                      {!noAnalysisYet && (
                        <MenuItem onClick={restartWalkthrough}>{t('tour.start')}</MenuItem>
                      )}
                      <MenuItem onClick={showWalkthroughVideo}>{t('walkthrough.video')}</MenuItem>
                      <MenuItem onClick={() => navigate('/fs/help')}>{t('go.to.faqs')}</MenuItem>
                    </Menu>
                  </div>
                )}
                <FsAccountMenu />
              </>
            )}
          </div>
        </Toolbar>
      </AppBar>
      {!fullScreen && (
        <Drawer
          key={`key_${location.pathname}`}
          variant="permanent"
          sx={{
            [`& .MuiDrawer-paper`]: {
              width: isMenuHovered ? 210 : 62,
              position: 'fixed',
              zIndex: 1200,
              backgroundColor: theme.palette.background.paper,
              borderRadius: 2,
              overflowX: 'hidden',
              margin: 0.5,
              px: 1,
              height: 'fit-content',
              maxHeight: 'calc(100vh - 84px)',
              top: 72,
              transition: 'width 0.25s',
              boxShadow: `0px 2px 4px -1px ${theme.palette.action.hover}, 0px 2px 2px 0px ${theme.palette.action.hover}, 0px 1px 10px 0px rgba(0, 0, 0, 0.12)`,
            },
          }}
          onMouseEnter={() => {
            setIsMenuHovered(true)
            setTimeout(() => {
              setIsShowText(true)
            }, 250)
          }}
          onMouseLeave={() => {
            setTimeout(() => {
              setIsShowText(false)
              setIsMenuHovered(false)
            }, 250)
          }}
        >
          <FsNavigation
            isHovered={isShowText}
            onClose={() => {
              setTimeout(() => {
                setIsShowText(false)
                setIsMenuHovered(false)
              }, 250)
            }}
          />
        </Drawer>
      )}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: 2,
          pb: 3,
          pl: 8.5,
          height: '100vh',
          width: `calc(100% - ${fullScreen ? 0 : 128}px)`,
          overflowY: 'hidden',
        }}
      >
        <Toolbar />
        <FsWalkthrough />
        <Container
          maxWidth="xl"
          sx={{ overflowY: 'hidden', height: 'calc(100vh - 89px)', pt: 1.5 }}
        >
          {largerThanMid ? (
            <Outlet />
          ) : (
            <Box>
              <Typography variant="h3" textAlign="center">
                {t('no.small.screen')}
              </Typography>
            </Box>
          )}
        </Container>
      </Box>
      {!account?.profile || account?.profile.isFirstTime ? (
        <Box
          height="100%"
          width="100%"
          position="absolute"
          top={0}
          left={0}
          sx={{ backgroundColor: '#00000080', zIndex: 1300 }}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Box
            py={4}
            width="400px"
            sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2 }}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <LanguageLogo color={theme.palette.primary.main} />
            <Typography mt={2} mx={4}>
              Wählen Sie Ihre gewünschte Sprache.
            </Typography>
            <Typography mx={4}>Choose your prefered language.</Typography>
            <Button
              variant="contained"
              sx={{
                mt: 2,
              }}
              onClick={() => {
                dispatch(
                  updateProfile({
                    profile: { preferredLanguage: 'de', isFirstTime: false },
                  }),
                )
                switchLanguage('de')
                dispatch(changeWalkthroughStatus(WALKTHROUGH_STATUS.InProgress))
              }}
            >
              Weiter auf Deutsch
            </Button>
            <Button
              variant="contained"
              sx={{
                mt: 2,
              }}
              onClick={() => {
                dispatch(
                  updateProfile({
                    profile: { preferredLanguage: 'en', isFirstTime: false },
                  }),
                )
                switchLanguage('en')
                dispatch(changeWalkthroughStatus(WALKTHROUGH_STATUS.InProgress))
              }}
            >
              Continue in English
            </Button>
          </Box>
        </Box>
      ) : null}
      <Dialog
        open={dateToChange !== null}
        onClose={() => setDateToChange(null)}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle id="responsive-dialog-title">{t('delete.confirm.title')}</DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <DateChangeIcon />
          {dateToChange && runs && (
            <DialogContentText mt={2} mx={5}>{`${t('date.confirm.message1')}${formatDate(
              runs.filter((r) => r.id === parseInt(dateToChange, 10))[0].runAt,
              currentLanguage,
              {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              },
            )}${t('date.confirm.message2')}`}</DialogContentText>
          )}
        </DialogContent>
        <DialogActions sx={{ pb: 2 }}>
          <Button variant="outlined" onClick={() => setDateToChange(null)}>
            {t('cancel')}
          </Button>
          <Button onClick={dateChangeConfirmed} variant="contained">
            {t('change.date')}
          </Button>
        </DialogActions>
      </Dialog>
      <FsDemoAccountModal isOpen={openDemoModal} setIsOpen={setOpenDemoModal} />
    </Box>
  )
}

export default FsDrawer
